class CartModal extends HTMLElement {
  constructor() {
    super();
    this._wishlistShareUrl = null;
    this._shareButtonsInitialised = false;
    this._wishlistItemsLoaded = false;
    this.bindGlobalEvents();
    this.updateInnerState();
    // Show the wishlist count badge as soon as WishlistKing is ready. The full
    // item enrichment/render is deferred until the cart or wishlist tab opens.
    this._whenWishlistKingReady(() => this.loadWishlistCount());
  }

  connectedCallback() {
    if (!this._shareButtonsInitialised) {
      this._initShareButtons();
      this._shareButtonsInitialised = true;
    }

    // Load wishlist items when the wishlist tab is opened (deferred for perf).
    if (!this._wishTabBound) {
      const wishTab = document.getElementById('c-tab-wish');
      if (wishTab) {
        wishTab.addEventListener('click', () => {
          if (!this._wishlistItemsLoaded) this.loadWishlistItems();
        });
        this._wishTabBound = true;
      }
    }
  }

  bindGlobalEvents() {
    document.addEventListener('cart:open', this.open.bind(this));

    document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      // Avoid intercepting the main product form which already has native JS (chunk.section-product-product.js) handling it along with upsells
      if (!form.hasAttribute('data-product-form')) {
          form.addEventListener('submit', this.onAddToCart.bind(this));
      } else {
          // Listen to the native form to trigger the modal to open once it's done
          form.addEventListener('submit', () => {
              const btn = form.querySelector('[name="add"]');
              if (btn) btn.classList.add('js-loading');
              
              // Poll for completion (the chunk script removes 'js-loading' when done adding)
              let attempts = 0;
              const checkClass = setInterval(async () => {
                  attempts++;
                  // The chunk script adds 'added' or removes 'js-loading'
                  if (!btn || btn.classList.contains('added') || !btn.classList.contains('js-loading') || attempts > 50) {
                      clearInterval(checkClass);
                      await this.refreshHTML();
                      this.open();
                      const wkSelected = document.querySelector('wishlist-button-product .wk-selected');
                      if (wkSelected) wkSelected.click();
                  }
              }, 100);
          });
      }
    });
  }

  updateInnerState() {
    this.cartItemCount = this.querySelectorAll('.product-item').length;

    this.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', this.close.bind(this));
    });

    this.querySelectorAll('.js-quantity-add').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const group = e.currentTarget.closest('.c-item-group');
        const input = e.currentTarget.closest('.c-qty').querySelector('input');
        input.stepUp();
        this.updateQuantity(input.dataset.key, input.value, group);
      });
    });

    this.querySelectorAll('.js-quantity-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const group = e.currentTarget.closest('.c-item-group');
        const input = e.currentTarget.closest('.c-qty').querySelector('input');
        input.stepDown();
        this.updateQuantity(input.dataset.key, input.value, group);
      });
    });

    this.querySelectorAll('.js-quantity-remove-all').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const group = e.currentTarget.closest('.c-item-group');
        this.updateQuantity(e.currentTarget.dataset.key, 0, group);
      });
    });

    const noteBtn   = this.querySelector('.js-cart-note-btn');
    const noteInput = this.querySelector('.js-cart-note-input');
    if (noteBtn && noteInput) {
      noteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.updateNote(noteInput.value);
      });
    }

    this.initAccordions();

    // Hide the shipping nudge when cart is empty, show it when items are present
    const cartNudge = this.querySelector('#c-panel-cart .c-nudge-wrap');
    if (cartNudge) cartNudge.style.display = this.cartItemCount === 0 ? 'none' : '';

    if (this.cartItemCount === 0) {
      this.fetchEmptyCollections();
    }
  }

  async updateQuantity(key, quantity, groupEl = null) {
    try {
      if (groupEl) groupEl.classList.add('c-loading');

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.refreshHTML();

    } catch (e) {
      console.error('Error updating quantity:', e);
      if (groupEl) groupEl.classList.remove('c-loading');
    }
  }

  async onAddToCart(event) {
    event.preventDefault();
    const form = event.target;
    const submitButton = form.querySelector('[type="submit"], button[name="add"]');
    if (submitButton) submitButton.disabled = true;

    try {
      await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
      await this.refreshHTML();
      this.open();
      const wkSelected = document.querySelector('wishlist-button-product .wk-selected');
      if (wkSelected) wkSelected.click();
    } catch (e) {
      console.error('Error adding to cart:', e);
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  async updateNote(note) {
    try {
      const btn = this.querySelector('.js-cart-note-btn');
      if (btn) btn.style.opacity = '0.5';

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });

      if (btn) btn.style.opacity = '1';
    } catch (e) {
      console.error('Error updating note:', e);
    }
  }

  async refreshHTML() {
    try {
      const res        = await fetch(window.location.pathname + '?section_id=header');
      const text       = await res.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
      const newCart    = parsedHTML.querySelector('cart-modal');

      if (!newCart) return;

      const oldBody = this.querySelector('.cart-modal-body');
      const newBody = newCart.querySelector('.cart-modal-body');
      if (oldBody && newBody) oldBody.innerHTML = newBody.innerHTML;

      const oldFooter = this.querySelector('.cart-modal-footer');
      const newFooter = newCart.querySelector('.cart-modal-footer');

      if (oldFooter && newFooter) {
        const newSubtotal = newFooter.querySelector('.total-row');
        const oldSubtotal = oldFooter.querySelector('.total-row');

        if (newSubtotal) {
          if (oldSubtotal) {
            oldSubtotal.innerHTML = newSubtotal.innerHTML;
          } else {
            oldFooter.innerHTML = newFooter.innerHTML;
          }
        } else {
          oldFooter.innerHTML = newFooter.innerHTML;
        }
      }

      const newBadgeCount = newCart.querySelector('#c-badge-cart');
      const oldBadgeCount = this.querySelector('#c-badge-cart');
      if (oldBadgeCount && newBadgeCount) {
        oldBadgeCount.textContent = newBadgeCount.textContent;
      }

      const newToggle = parsedHTML.querySelector('.js-toggle-cart-modal');
      const oldToggle = document.querySelector('.js-toggle-cart-modal');
      if (newToggle && oldToggle) oldToggle.innerHTML = newToggle.innerHTML;

      this.querySelectorAll('.c-loading').forEach(el => el.classList.remove('c-loading'));

      this.updateInnerState();

    } catch (e) {
      console.error(e);
      window.location.reload();
    }
  }

  async fetchEmptyCollections() {
    const container = this.querySelector('#CartPopupEmptyCollections');
    if (!container) return;

    try {
      const response = await fetch('/search?view=cart_collections');
      const data     = await response.json();
      container.innerHTML = data
        .map(item => `<a class="button button-primary" href="${item.url}">Shop ${item.title}</a>`)
        .join('');
    } catch (e) {
      console.warn(e);
    }
  }

  initAccordions() {
    this.querySelectorAll('.js-accordion-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const body = btn.nextElementSibling;
        if (body) body.classList.toggle('closed');
      });
    });
  }

  open() {
    this.classList.add('open');
    if (!this._wishlistItemsLoaded) this.loadWishlistItems();
  }

  close() {
    this.classList.remove('open');
    // Allow a fresh items load on the next open (picks up external changes).
    this._wishlistItemsLoaded = false;
  }

  // ── Resolve WishlistKing API headers / shop (shared by count + items) ────────
  _wkHeaders() {
    return (window.WishlistKing
      && window.WishlistKing._apis
      && window.WishlistKing._apis.wishlist
      && window.WishlistKing._apis.wishlist.headers) || null;
  }

  _wkShop(wkHeaders) {
    return (wkHeaders && wkHeaders['x-appmate-shp'])
      || this.dataset.shop
      || (window.Shopify && window.Shopify.shop)
      || window.location.hostname;
  }

  // Poll until WishlistKing has initialised, then run the callback (~12s max).
  _whenWishlistKingReady(cb, attempts = 0) {
    if (this._wkHeaders()) { cb(); return; }
    if (attempts > 40) return;
    setTimeout(() => this._whenWishlistKingReady(cb, attempts + 1), 300);
  }

  // ── Lightweight: fetch the wishlist and set only the count badge ─────────────
  async loadWishlistCount() {
    const badge = document.getElementById('c-badge-wish');
    if (!badge) return;

    const wkHeaders = this._wkHeaders();
    if (!wkHeaders) return;
    const shop = this._wkShop(wkHeaders);

    try {
      const res = await fetch(`https://api.appmate.io/v2/wishlists/mine?shop=${encodeURIComponent(shop)}`, {
        credentials: 'include',
        headers: wkHeaders
      });
      if (!res.ok) return;

      const data  = await res.json();
      const items = data.items || (data.wishlist && data.wishlist.items) || [];
      const count = (data.wishlist && typeof data.wishlist.numItems === 'number')
        ? data.wishlist.numItems
        : items.length;
      badge.textContent = count;

      // Cache the share URL while we have the payload.
      const publicId = data.wishlist && data.wishlist.publicId;
      if (publicId) this._wishlistShareUrl = window.location.origin + '/apps/wishlist/' + publicId;
    } catch (e) {
      console.error('[Wishlist] Count load failed:', e);
    }
  }

    async loadWishlistItems() {
    const container = this.querySelector('#wish-items');
    const badge     = document.getElementById('c-badge-wish');
    console.log('[Wishlist] loadWishlistItems called', { container, badge });
    if (!container) { console.warn('[Wishlist] #wish-items not found — aborting'); return; }

    try {
      const wkHeaders = window.WishlistKing
        && window.WishlistKing._apis
        && window.WishlistKing._apis.wishlist
        && window.WishlistKing._apis.wishlist.headers;

      console.log('[Wishlist] WishlistKing._apis.wishlist.headers:', wkHeaders);

      if (!wkHeaders) {
        console.warn('[Wishlist] WishlistKing._apis.wishlist.headers not available — aborting');
        return;
      }

      const shop = wkHeaders['x-appmate-shp']
        || this.dataset.shop
        || (window.Shopify && window.Shopify.shop)
        || window.location.hostname;

      const url = `https://api.appmate.io/v2/wishlists/mine?shop=${encodeURIComponent(shop)}`;
      console.log('[Wishlist] Fetching:', url, { headers: wkHeaders });

      const res = await fetch(url, { credentials: 'include', headers: wkHeaders });
      console.log('[Wishlist] Response status:', res.status, res.statusText);

      if (!res.ok) {
        console.warn('[Wishlist] Non-OK response — aborting render');
        return;
      }

      const data  = await res.json();
      console.log('[Wishlist] Raw API data:', data);

      // Build shareable URL from publicId (e.g. /apps/wishlist/69cf836b77878f1c4dc8db68)
      const publicId = data.wishlist && data.wishlist.publicId;
      console.log('[Wishlist] publicId for sharing:', publicId);
      this._wishlistShareUrl = window.location.origin + '/apps/wishlist'
        + (publicId ? '/' + publicId : '');
      console.log('[Wishlist] Share URL:', this._wishlistShareUrl);

      const items = data.items || (data.wishlist && data.wishlist.items) || [];
      console.log('[Wishlist] Parsed items (' + items.length + '):', items);

      if (badge) {
        badge.textContent = items.length;
        console.log('[Wishlist] Badge updated to', items.length);
      }

      const wishNudge = this.querySelector('#c-panel-wish .c-nudge-wrap');

      if (!items.length) {
        console.log('[Wishlist] No items — rendering empty state');
        if (wishNudge) wishNudge.style.display = 'none';
        container.innerHTML = `
          <div class="text-center cart-modal-cta">
            <div class="label2">You have no Saved Items</div>
          </div>`;
        this._wishlistItemsLoaded = true;
        return;
      }

      if (wishNudge) wishNudge.style.display = '';

      // Enrich all items in one Shopify Storefront API batch call
      console.log('[Wishlist] Enriching', items.length, 'item(s) via Shopify Storefront API...');
      const enriched = await this._enrichAllItems(items);
      console.log('[Wishlist] Enriched items:', enriched);

      console.log('[Wishlist] Rendering', enriched.length, 'item(s)');
      container.innerHTML = enriched
        .map((item, i) => this._renderWishItem(item, i === enriched.length - 1))
        .join('');

      container.querySelectorAll('.c-action-remove').forEach(btn => {
        btn.addEventListener('click', async () => {
          btn.disabled = true;
          await this._removeWishlistItem(btn.dataset.itemId);
          this.loadWishlistItems();
        });
      });

      // ADD TO CART → add the wishlist item directly to the cart.
      container.querySelectorAll('.wish-add-to-cart').forEach(btn => {
        btn.addEventListener('click', () => this.addWishlistItemToCart(btn));
      });

      this._wishlistItemsLoaded = true;

    } catch (e) {
      console.error('[Wishlist] Failed to load:', e);
    }
  }

  // ── Add a wishlist item to the cart ─────────────────────────────────────────
  // Uses the item's variantId when present; otherwise resolves the product's
  // first available variant from its handle. Once added, the item is removed
  // from the wishlist.
  async addWishlistItemToCart(btn) {
    let variantId = btn.dataset.variantId;
    const handle  = btn.dataset.handle;
    const itemId  = btn.dataset.itemId;
    const group   = btn.closest('.c-item-group');

    btn.disabled = true;
    if (group) group.classList.add('c-loading');
    try {
      if (!variantId && handle) {
        const res = await fetch('/products/' + handle + '.js', { credentials: 'same-origin' });
        if (res.ok) {
          const product = await res.json();
          const variant = (product.variants || []).find(v => v.available) || (product.variants || [])[0];
          if (variant) variantId = variant.id;
        }
      }

      if (!variantId) {
        console.warn('[Wishlist] No variant available to add to cart');
        if (group) group.classList.remove('c-loading');
        btn.disabled = false;
        return;
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      await this.refreshHTML();

      // Remove the item from the wishlist now that it's in the cart.
      if (itemId) await this._removeWishlistItem(itemId);
      this.loadWishlistItems();

      switchDrawerTab('cart');
      this.open();
    } catch (e) {
      console.error('[Wishlist] Add to cart failed:', e);
      if (group) group.classList.remove('c-loading');
      btn.disabled = false;
    }
  }

  // ── Remove an item from the Appmate wishlist by its wishlistItemId ───────────
  async _removeWishlistItem(wishlistItemId) {
    if (!wishlistItemId) return;

    const wkHeaders = window.WishlistKing
      && window.WishlistKing._apis
      && window.WishlistKing._apis.wishlist
      && window.WishlistKing._apis.wishlist.headers;

    const shop = (wkHeaders && wkHeaders['x-appmate-shp'])
      || (window.Shopify && window.Shopify.shop)
      || window.location.hostname;

    try {
      const res = await fetch(`https://api.appmate.io/v2/wishlists/mine/items?shop=${encodeURIComponent(shop)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...wkHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ wishlistItemId })
      });
      console.log('[Wishlist] Remove response:', res.status, res.statusText);
    } catch (e) {
      console.error('[Wishlist] Remove failed:', e);
    }
  }

  // ── Enrich all wishlist items in one Shopify Storefront GraphQL batch ──────
  async _enrichAllItems(items) {
    const productIds = items.map(i => i.productId || i.product_id);
    const variantIds = items.map(i => i.variantId || i.variant_id).filter(Boolean);
    const { products: productMap, variants: variantMap } =
      await this._fetchProductsFromStorefront(productIds, variantIds);
    console.log('[Wishlist] Product map:', productMap, 'Variant map:', variantMap);

    return items.map(item => {
      const productId = item.productId || item.product_id;
      const variantId = item.variantId || item.variant_id;
      const p         = productMap[String(productId)] || {};
      const v         = variantId ? (variantMap[String(variantId)] || {}) : {};

      const title  = p.title  || '';
      const handle = p.handle || '';
      const vendor = p.vendor || '';
      // Prefer the saved variant's own image, then the product image.
      const image  = (v.image && v.image.url)
        || (p.featuredImage && p.featuredImage.url) || '';
      // Storefront API returns price as decimal string e.g. "19.99" → convert to cents for _money()
      // Use the saved variant's own price; only fall back to the product's
      // minimum price when the item has no variant.
      const priceAmt = (v.price && v.price.amount)
        || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount);
      const price  = priceAmt != null ? Math.round(parseFloat(priceAmt) * 100) : null;

      // Variant option values (e.g. ["iPhone 17", "Armoured"]), excluding the
      // implicit single-variant "Default Title".
      const variantOptions = (v.selectedOptions || [])
        .map(o => o && o.value)
        .filter(val => val && val !== 'Default Title');

      // Products with real options (anything beyond the implicit "Title")
      // need a variant chosen. Items saved from collection pages have no
      // variantId, so send those users to the product page instead.
      const productOptions = (p.options || []).map(o => o && o.name).filter(Boolean);
      const hasRealVariants = productOptions.length > 1
        || (productOptions.length === 1 && productOptions[0] !== 'Title');
      // A variant only counts as "selected" if it actually resolved via the
      // Storefront API — a stale/deleted variantId must not enable Add to cart.
      const variantResolved = !!(variantId && v.id);
      // When the product node itself didn't resolve we can't know its options,
      // so without a resolved variant the only safe action is "View details".
      const needsVariantSelection = hasRealVariants
        ? !variantResolved
        : (!variantResolved && !p.id);

      const base = handle ? '/products/' + handle : '/products/' + productId;
      const url  = base + (variantId ? '?variant=' + variantId : '');

      console.log('[Wishlist] Enriched:', { productId, title, handle, price, image, url, variantOptions, needsVariantSelection });
      return { ...item, productId, variantId, title, handle, vendor, price, image, url, variantOptions, needsVariantSelection };
    });
  }

  // ── Single GraphQL batch request → maps of productId → node, variantId → node ─
  async _fetchProductsFromStorefront(productIds, variantIds = []) {
    const token = this._getStorefrontToken();
    console.log('[Wishlist] Storefront token:', token ? token.slice(0, 8) + '…' : 'NOT FOUND');
    console.log('[Wishlist] WishlistKing._apis:', window.WishlistKing && window.WishlistKing._apis);

    if (!token) {
      console.warn('[Wishlist] No storefront token found — cannot fetch product data');
      return { products: {}, variants: {} };
    }

    const productGids = productIds.map(id => 'gid://shopify/Product/' + id);
    const variantGids = variantIds.map(id => 'gid://shopify/ProductVariant/' + id);
    const query = `
      query getWishlistData($productIds: [ID!]!, $variantIds: [ID!]!, $country: CountryCode)
      @inContext(country: $country) {
        products: nodes(ids: $productIds) {
          ... on Product {
            id
            title
            handle
            vendor
            featuredImage { url }
            priceRange { minVariantPrice { amount currencyCode } }
            options { name }
          }
        }
        variants: nodes(ids: $variantIds) {
          ... on ProductVariant {
            id
            selectedOptions { name value }
            price { amount currencyCode }
            image { url }
          }
        }
      }`;

    const apiUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/')
      + 'api/' + this._getStorefrontVersion() + '/graphql.json';

    console.log('[Wishlist] Storefront GraphQL URL:', apiUrl);
    console.log('[Wishlist] Querying product GIDs:', productGids, 'variant GIDs:', variantGids);

    const res  = await fetch(apiUrl, {
      method:  'POST',
      headers: {
        'Content-Type':                       'application/json',
        'Shopify-Storefront-Access-Token':     token
      },
      body: JSON.stringify({
        query,
        variables: { productIds: productGids, variantIds: variantGids, country: this._getMarketCountry() }
      })
    });

    console.log('[Wishlist] Storefront response status:', res.status);
    const data = await res.json();
    console.log('[Wishlist] Storefront response data:', data);

    const products = {};
    const variants = {};
    if (data.data) {
      (data.data.products || []).forEach(node => {
        if (!node || !node.id) return;
        products[node.id.replace('gid://shopify/Product/', '')] = node;
      });
      (data.data.variants || []).forEach(node => {
        if (!node || !node.id) return;
        variants[node.id.replace('gid://shopify/ProductVariant/', '')] = node;
      });
    }
    return { products, variants };
  }

  // ── Detect Shopify Storefront access token from any available source ────────
  _getStorefrontToken() {
    // WishlistKing.config.storefront (primary — confirmed present)
    const wk = window.WishlistKing;
    if (wk && wk.config && wk.config.storefront && wk.config.storefront.accessToken) {
      return wk.config.storefront.accessToken;
    }

    // WishlistKing._apis fallback
    if (wk && wk._apis) {
      const sf = wk._apis.storefront;
      if (sf) {
        const h = sf.headers || {};
        const t = h['Shopify-Storefront-Access-Token']
               || h['x-shopify-storefront-access-token']
               || sf.token;
        if (t) return t;
      }
    }

    // Shopify globals
    const S = window.Shopify;
    if (S && S.storefrontToken)         return S.storefrontToken;
    if (S && S.storefront_access_token) return S.storefront_access_token;

    // Meta tag
    const m = document.querySelector('meta[name="shopify-storefront-token"], meta[name="storefront-access-token"]');
    if (m) return m.getAttribute('content');

    return null;
  }

  // ── API version sourced from WishlistKing.config.storefront.version ─────────
  _getStorefrontVersion() {
    const wk = window.WishlistKing;
    return (wk && wk.config && wk.config.storefront && wk.config.storefront.version) || '2024-10';
  }

  // ── Visitor's market country so Storefront prices match the active currency ──
  _getMarketCountry() {
    if (window.Shopify && window.Shopify.country) return window.Shopify.country;

    // theme.liquid stamps the body with sitecntrycode-{{ localization.country.iso_code }}
    const m = document.body.className.match(/sitecntrycode-([A-Z]{2})/i);
    if (m) return m[1].toUpperCase();

    return null;
  }

  _renderWishItem(item, isLast) {
    const img     = item.image || '';
    const title   = this._esc(item.title || '');
    const vendor  = this._esc(item.vendor || '');
    const price   = item.price != null ? this._money(item.price) : '';
    const url     = item.url || '#';

    // Show variant option values (e.g. iPhone 17 / Armoured) when present,
    // matching the cart item layout; otherwise fall back to the vendor.
    const variantOptions = item.variantOptions || [];
    const subHtml = variantOptions.length
      ? variantOptions.map(v => `<p class="c-item-sub mb-0">${this._esc(v)}</p>`).join('')
      : (vendor ? `<p class="c-item-sub mb-0">${vendor}</p>` : '');

    return `
      <div class="c-item-group product-item"
           data-product-id="${item.productId || ''}"
           data-variant-id="${item.variantId || ''}">
        <div class="c-item-row d-flex gap-3">
          ${img
            ? `<div class="c-item-thumb flex-shrink-0 overflow-hidden">
                 <a href="${url}" aria-hidden="true" tabindex="-1">
                   <img src="${img}" alt="${title}" loading="lazy">
                 </a>
               </div>`
            : '<div class="c-item-thumb flex-shrink-0 overflow-hidden"></div>'}
          <div class="c-item-meta d-flex flex-column justify-content-between flex-grow-1">
            <div class="c-item-top d-flex flex-column gap-1">
              <div class="c-item-headline d-flex align-items-start justify-content-between">
                <span class="c-item-name semibold">
                  <a href="${url}">${title}</a>
                </span>
                ${price ? `<span class="c-item-price semibold">${price}</span>` : ''}
              </div>
              ${subHtml}
            </div>
            <div class="c-item-controls d-flex align-items-center justify-content-between">
              ${item.needsVariantSelection
                ? `<a href="${url}" class="c-action-link">VIEW DETAILS</a>`
                : `<button type="button" class="c-action-link wish-add-to-cart"
                    data-variant-id="${item.variantId || ''}"
                    data-product-id="${item.productId || ''}"
                    data-item-id="${item.id || ''}"
                    data-handle="${item.handle || ''}">ADD TO CART</button>`}
              <button type="button" class="c-action-remove"
                data-product-id="${item.productId || ''}"
                data-variant-id="${item.variantId || ''}"
                data-item-id="${item.id || ''}"
                data-handle="${item.handle || ''}">REMOVE</button>
            </div>
          </div>
        </div>

        <div class="spinner-container">
          <div class="spinner" title="Loading" style="font-size:32px"></div>
        </div>
      </div>
      ${!isLast ? '<hr class="c-divider">' : ''}`;
  }

  // ── Wire up the three wishlist share buttons (called once from connectedCallback) ──
  _initShareButtons() {
    const getUrl = () => this._wishlistShareUrl || (window.location.origin + '/apps/wishlist');

    const whatsappBtn = this.querySelector('#c-share-whatsapp');
    if (whatsappBtn) {
      whatsappBtn.addEventListener('click', () => {
        const url  = getUrl();
        const text = encodeURIComponent('Check out my wishlist! ');
        window.open('https://wa.me/?text=' + text + encodeURIComponent(url), '_blank');
      });
    }

    const emailBtn = this.querySelector('#c-share-email');
    if (emailBtn) {
      emailBtn.addEventListener('click', () => {
        const url  = getUrl();
        const subj = encodeURIComponent('My Wishlist');
        const body = encodeURIComponent('Hey! I wanted to share my wishlist with you: ' + url);
        window.location.href = 'mailto:?subject=' + subj + '&body=' + body;
      });
    }

    const copyBtn = this.querySelector('#c-share-copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const url   = getUrl();
        const flash = () => {
          const orig = copyBtn.innerHTML;
          copyBtn.innerHTML = copyBtn.innerHTML.replace('COPY LINK', 'COPIED!');
          setTimeout(() => { copyBtn.innerHTML = orig; }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(flash).catch(() => {
            this._copyFallback(url);
            flash();
          });
        } else {
          this._copyFallback(url);
          flash();
        }
      });
    }
  }

  _copyFallback(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) { /* silent */ }
    document.body.removeChild(ta);
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _money(cents) {
    if (cents == null) return '';
    const num = typeof cents === 'string' ? parseFloat(cents) : cents;
    const amt = num / 100;

    // Prefer the theme's session-rendered money format (follows the active
    // market currency); the WishlistKing app config is static base-currency.
    const fmt = (window.theme && window.theme.moneyFormat)
      || (window.WishlistKing && window.WishlistKing.config
      && window.WishlistKing.config.shop && window.WishlistKing.config.shop.moneyFormat)
      || '${{amount}}';

    // Shopify supports four amount placeholder variants
    return fmt
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amt).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_with_comma_separator}}',              amt.toFixed(2).replace('.', ','))
      .replace('{{amount_no_decimals}}',                       Math.round(amt).toLocaleString())
      .replace('{{amount}}',                                   amt.toFixed(2));
  }
}

customElements.define('cart-modal', CartModal);

function switchDrawerTab(tab) {
  var isCart = tab === 'cart';
  var tCart  = document.getElementById('c-tab-cart');
  var tWish  = document.getElementById('c-tab-wish');
  var pCart  = document.getElementById('c-panel-cart');
  var pWish  = document.getElementById('c-panel-wish');

  if (!tCart || !tWish || !pCart || !pWish) return;

  tCart.className = tCart.className.replace(/c-is-\S+/, isCart ? 'c-is-active' : 'c-is-inactive');
  tWish.className = tWish.className.replace(/c-is-\S+/, isCart ? 'c-is-inactive' : 'c-is-active');
  tCart.setAttribute('aria-selected', isCart);
  tWish.setAttribute('aria-selected', !isCart);
  pCart.className = 'c-panel overflow-hidden' + (isCart ? ' c-is-active' : '');
  pWish.className = 'c-panel overflow-hidden' + (isCart ? '' : ' c-is-active');
}

document.addEventListener('DOMContentLoaded', function () {
  ['c-tab-cart', 'c-tab-wish'].forEach(function (id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        switchDrawerTab(id === 'c-tab-cart' ? 'cart' : 'wish');
      }
    });
  });
});